import { http } from '@/api/http';
import type { OperationResult, Schemas } from '@/api/shared';

export type NoteInputDto = Schemas['NoteInput'];

export const notesApi = {
  /** GET /persons/{guid}/notes — clinical notes and communications. */
  getNotes: (
    guid: string,
    options?: { signal?: AbortSignal },
  ): Promise<OperationResult<'getNotes'>> =>
    http.get<OperationResult<'getNotes'>>(
      `/persons/${encodeURIComponent(guid)}/notes`,
      options,
    ),

  /** POST /persons/{guid}/notes — add a note. Body matches NoteInput. */
  addNote: (
    guid: string,
    body: Schemas['NoteInput'],
    options?: { signal?: AbortSignal },
  ): Promise<Schemas['Note']> =>
    http.post<Schemas['Note']>(
      `/persons/${encodeURIComponent(guid)}/notes`,
      body,
      options,
    ),

  /**
   * PUT /persons/{guid}/notes/{noteGuid} — edit an existing note. Only the
   * authoring user is allowed (BE enforces). Used by the Notes section
   * within the configured edit window (PTV2-1106 / Phase 1 Notes spec).
   */
  updateNote: (
    guid: string,
    noteGuid: string,
    body: Schemas['NoteInput'],
    options?: { signal?: AbortSignal },
  ): Promise<Schemas['Note']> =>
    http.put<Schemas['Note']>(
      `/persons/${encodeURIComponent(guid)}/notes/${encodeURIComponent(noteGuid)}`,
      body,
      options,
    ),

  /**
   * DELETE /persons/{guid}/notes/{noteGuid} — soft-delete an existing note.
   * BE marks `is_deleted = true` and keeps the row for audit; the response
   * is the now-deleted Note so the FE can drop it from the list.
   */
  deleteNote: (
    guid: string,
    noteGuid: string,
    options?: { signal?: AbortSignal },
  ): Promise<Schemas['Note']> =>
    http.delete<Schemas['Note']>(
      `/persons/${encodeURIComponent(guid)}/notes/${encodeURIComponent(noteGuid)}`,
      options,
    ),
};
